// src/pages/employer/EmployerMatchDetail.jsx — Candidate Match Review (V2 scoreBreakdown)
// Three-column layout per row: Position raw value | Candidate raw value | Score
// Temporary verbose mode: surfaces every text pair sent to the embedding model.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobAdAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';

const BAND_COLOURS = { EXCELLENT: '#28A745', GOOD: '#3D80CC', FAIR: '#E6A817', POOR: '#CC0000' };

const PREF_LABELS = {
  workplaceEnvironment: 'Workplace Environment',
  workplaceSize:        'Workplace Size',
  workplaceLocation:    'Workplace Location',
  positionName:         'Position Name',
  adminLevel:           'Admin Level',
  employerName:         'Employer Name',
  employerType:         'Employer Type',
  industry:             'Industry',
  schedule:             'Schedule',
  salary:               'Salary',
  benefits:             'Benefits',
};

/* ── Formatters ──────────────────────────────────────────── */

const fmtCurrency = (n, currency = 'AUD') => {
  if (n == null) return '—';
  try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${n}`; }
};

const fmtSalaryRange = ({ min, max, period, currency = 'AUD' } = {}) => {
  if (min == null && max == null) return '—';
  const range = min != null && max != null
    ? `${fmtCurrency(min, currency)} — ${fmtCurrency(max, currency)}`
    : fmtCurrency(min ?? max, currency);
  return period ? `${range} / ${period.toLowerCase()}` : range;
};

const fmtAddress = (addr) => {
  if (!addr || typeof addr !== 'object') return '—';
  const parts = [
    addr.line1, addr.line2,
    addr.suburb || addr.city, addr.state, addr.postcode, addr.country,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const fmtList = (v) => {
  if (v == null) return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const fmtLocations = (locs) => {
  if (!Array.isArray(locs) || !locs.length) return '—';
  return locs.map(l => [l.city || l.suburb, l.state, l.country].filter(Boolean).join(', ')).filter(Boolean).join(' • ');
};

const cleanHuman = (v) => (v == null ? '—' : String(v).replace(/_/g, ' '));

/* ── Reusable presentational components ──────────────────── */

const scoreColour = (s) =>
  s >= 80 ? '#28A745' : s >= 60 ? '#3D80CC' : s >= 40 ? '#E6A817' : '#CC0000';

const ScoreBadge = ({ score, suffix = '' }) => {
  if (score == null) return <span style={{ fontSize: 12, color: '#999' }}>n/a</span>;
  const s = Math.round(score);
  return (
    <span style={{
      display: 'inline-block', minWidth: 38, padding: '2px 8px', borderRadius: 10,
      fontSize: 12, fontWeight: 700, textAlign: 'center',
      background: `${scoreColour(s)}18`, color: scoreColour(s),
    }}>{s}{suffix}</span>
  );
};

const ScoreBar = ({ label, score, weight, weightedScore }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{label}</span>
      <span style={{ fontSize: 12, color: '#666' }}>
        {Math.round(score)}/100 × {(weight * 100).toFixed(0)}% = <strong>{weightedScore.toFixed(1)}</strong>
      </span>
    </div>
    <div style={{ height: 10, background: '#e9ecef', borderRadius: 5, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${Math.min(score, 100)}%`, borderRadius: 5,
        background: scoreColour(score), transition: 'width 0.4s ease',
      }} />
    </div>
  </div>
);

const COL_HEAD = { fontSize: 11, fontWeight: 700, color: '#3D80CC', textTransform: 'uppercase', padding: '8px 10px', borderBottom: '1px solid #ddd' };

const GRID_COLS = '1fr 1fr 80px 100px';

const ThreeColTable = ({ children }) => (
  <div style={{ border: '1px solid #e5e5e5', borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
    <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, background: '#f7f9fc' }}>
      <div style={COL_HEAD}>Position value</div>
      <div style={COL_HEAD}>Candidate value</div>
      <div style={{ ...COL_HEAD, textAlign: 'center' }}>Score</div>
      <div style={{ ...COL_HEAD, textAlign: 'center' }}>Overall contribution</div>
    </div>
    {children}
  </div>
);

const Contribution = ({ value }) => {
  if (value == null) return <span style={{ fontSize: 11, color: '#bbb' }}>—</span>;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: '#0e4e95' }}>
      {value.toFixed(2)} pts
    </span>
  );
};

const ThreeColRow = ({ label, position, candidate, score, scoreSuffix = '', contribution, muted = false, mono = false }) => (
  <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, borderBottom: '1px solid #f0f0f0' }}>
    {label && (
      <div style={{ gridColumn: '1 / -1', padding: '6px 10px', background: '#fafafa', fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
    )}
    <Cell mono={mono} muted={muted}>{position}</Cell>
    <Cell mono={mono} muted={muted}>{candidate}</Cell>
    <div style={{ padding: '10px', textAlign: 'center', alignSelf: 'center' }}>
      <ScoreBadge score={score} suffix={scoreSuffix} />
    </div>
    <div style={{ padding: '10px', textAlign: 'center', alignSelf: 'center' }}>
      <Contribution value={contribution} />
    </div>
  </div>
);

const Cell = ({ children, mono, muted }) => (
  <div style={{
    padding: '10px', fontSize: 13, color: muted ? '#999' : '#333',
    fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderRight: '1px solid #f0f0f0',
  }}>
    {children == null || children === '' ? <em style={{ color: '#bbb' }}>—</em> : children}
  </div>
);

const Section = ({ title, score, weightLabel, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="section-box" style={{ marginBottom: 16 }}>
      <div className="section-box-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {title}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700 }}>
          {weightLabel && <span style={{ fontWeight: 400, opacity: 0.85, marginRight: 8 }}>{weightLabel}</span>}
          {score != null && `${Math.round(score)}/100`}
        </span>
      </div>
      {open && <div className="section-box-body" style={{ padding: '14px 18px' }}>{children}</div>}
    </div>
  );
};

const SubCard = ({ title, score, breakdown, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid #d8e2ee', borderRadius: 6, marginBottom: 12, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ background: '#4A6FA5', color: '#fff', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {title}
        </span>
        <span>{score != null ? `${Math.round(score)}/100` : '—'}</span>
      </div>
      {open && (
        <div style={{ padding: 12 }}>
          {breakdown?.reason && (
            <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic', marginBottom: 8 }}>{breakdown.reason}</div>
          )}
          {children}
          {breakdown && (breakdown.preboostScore != null) && (
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#f7f9fc', borderRadius: 4, fontSize: 12, color: '#555' }}>
              Pre-boost: <strong>{breakdown.preboostScore}</strong>
              {breakdown.durationMultiplier != null && <> · duration ×{breakdown.durationMultiplier}</>}
              {breakdown.educationMultiplier != null && <> · education ×{breakdown.educationMultiplier}</>}
              {breakdown.educationLevelMultiplier != null && <> · level ×{breakdown.educationLevelMultiplier}</>}
              {breakdown.adminLevelMultiplier != null && <> · admin ×{breakdown.adminLevelMultiplier}</>}
              {breakdown.scheduleMultiplier != null && <> · schedule ×{breakdown.scheduleMultiplier}</>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Embedding-grade renderer ────────────────────────────── */

const GRADE_LABELS = {
  grade1: 'Grade 1 — Collated',
  grade2: 'Grade 2 — Per item',
  grade3: 'Grade 3 — Per role / source-type',
  grade4: 'Grade 4 — Per task / source-item',
  grade5: 'Grade 5 — Individual',
};

const EmbeddingGradeRows = ({ gradeScores, scaleFactor = 0 }) => {
  const keys = Object.keys(gradeScores || {});
  if (!keys.length) return <div style={{ fontSize: 12, color: '#999' }}>No grades enabled.</div>;

  return (
    <ThreeColTable>
      {keys.map(key => {
        const g = gradeScores[key];
        if (!g) return null;

        // Single-pair grade
        if (g.seekerText !== undefined || g.positionText !== undefined) {
          const contrib = g.raw != null ? g.raw * g.weight * scaleFactor : null;
          return (
            <ThreeColRow key={key}
              label={`${GRADE_LABELS[key] || key} · weight ${(g.weight * 100).toFixed(0)}%`}
              position={g.positionText}
              candidate={g.seekerText}
              score={g.raw} scoreSuffix="%"
              contribution={contrib}
              mono />
          );
        }

        // Multi-pair grade — render per pair
        const pairs = Array.isArray(g.pairs) ? g.pairs : [];
        const sTexts = g.seekerTexts || {};
        const pTexts = g.positionTexts || {};
        const gradeContrib = g.raw != null ? g.raw * g.weight * scaleFactor : null;
        return (
          <React.Fragment key={key}>
            <div style={{ gridColumn: '1 / -1', padding: '6px 10px', background: '#eef3fa', fontSize: 11, fontWeight: 700, color: '#0e4e95', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {GRADE_LABELS[key] || key} · weight {(g.weight * 100).toFixed(0)}% · aggregate {g.raw}%
              {gradeContrib != null && <> · contributes {gradeContrib.toFixed(2)} pts</>}
            </div>
            {pairs.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', padding: 10, fontSize: 12, color: '#999' }}>No pair data.</div>
            ) : pairs.map((p, i) => (
              <ThreeColRow key={`${key}-${i}`}
                position={pTexts[p.idB]}
                candidate={sTexts[p.idA]}
                score={p.similarity * 100} scoreSuffix="%"
                mono />
            ))}
          </React.Fragment>
        );
      })}
    </ThreeColTable>
  );
};

/* ── Main page ───────────────────────────────────────────── */

export const EmployerMatchDetail = () => {
  const { matchId } = useParams();
  const { activeOrg, activeProfile } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!activeOrg || !activeProfile) return;
    jobAdAPI.getCandidateDetail(activeOrg.id, activeProfile.id, matchId)
      .then(({ data }) => setMatch(data.data.match))
      .catch(() => navigate('/employer/candidates/reviews', { replace: true }))
      .finally(() => setLoading(false));
  }, [activeOrg, activeProfile, matchId]); // eslint-disable-line

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;
  if (!match) return null;

  const seeker    = match.jobSeeker || {};
  const position  = match.adPosition || {};
  const ad        = position.jobAd || {};
  const groupProf = ad.groupProfile || {};
  const workplace = position.workplace || {};
  const posName   = position.jobDefinition?.name || 'Position';
  const breakdown = match.scoreBreakdown || {};
  const topW      = breakdown.topWeights || {};
  const subW      = breakdown.subWeights || {};
  const circ      = breakdown.circumstances || {};
  const prefs     = breakdown.preferences || {};
  const sa        = breakdown.skillsAbilities || {};
  const sub       = sa.sub || {};

  const topScores = [
    { key: 'circumstances',   label: 'Circumstances',     score: circ.score ?? match.circumstanceScore, weight: topW.circumstances || 0 },
    { key: 'preferences',     label: 'Seeker Preferences', score: prefs.score ?? match.locationScore,   weight: topW.preferences || 0 },
    { key: 'skillsAbilities', label: 'Skills & Abilities', score: sa.score ?? match.skillScore,         weight: topW.skillsAbilities || 0 },
  ];
  const totalWeighted = topScores.reduce((acc, s) => acc + (s.score || 0) * (s.weight || 0), 0);

  /* ── Render Circumstances ───────────────────────────────── */
  const matchScore = (a, b) => {
    if (!a || !b) return undefined;
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase() ? 100 : 0;
  };

  // Internal weights within the Circumstances scorer — read from breakdown (model-driven)
  // Work rights is flag-only (no contribution); availability + salary are renormalized by the backend
  const circIW = circ.internalWeights || { availabilityWeight: 0.667, salaryWeight: 0.333 };
  const CIRC_INTERNAL = { availability: circIW.availabilityWeight, salary: circIW.salaryWeight };
  const circTopW = topW.circumstances || 0;

  const renderCircumstances = () => {
    const wr  = circ.workRights || {};
    const av  = circ.availability || {};
    const sal = circ.salary || {};
    const res = circ.residence || {};
    const contribution = (score, internal) =>
      score == null ? null : score * internal * circTopW;
    const countryScore = matchScore(res.positionCountry, res.candidateCountry);
    // Cascading: if country mismatches (0), state is also a mismatch.
    const stateScore = countryScore === 0
      ? 0
      : matchScore(res.positionState, res.candidateState);
    // Cascading: if country or state mismatches (0), city is also a mismatch.
    const cityScore = (countryScore === 0 || stateScore === 0)
      ? 0
      : matchScore(res.positionCity, res.candidateCity);
    return (
      <ThreeColTable>
        <ThreeColRow label="Work Rights (flag only)"
          position={wr.positionRequirement}
          candidate={cleanHuman(wr.candidateStatus)}
          score={wr.score} />
        <ThreeColRow label="Availability"
          position={cleanHuman(av.positionRequirement)}
          candidate={cleanHuman(av.candidateStatus)}
          score={av.score}
          contribution={contribution(av.score, CIRC_INTERNAL.availability)} />
        <ThreeColRow label="Salary"
          position={fmtSalaryRange({ min: sal.positionMin, max: sal.positionMax, period: sal.positionPeriod, currency: sal.positionCurrency })}
          candidate={fmtSalaryRange({ min: sal.candidateMin, max: sal.candidateMax, currency: sal.positionCurrency })}
          score={sal.score}
          contribution={contribution(sal.score, CIRC_INTERNAL.salary)} />
        <ThreeColRow label="Residence — Country (flag only)"
          position={res.positionCountry}
          candidate={res.candidateCountry}
          score={countryScore} />
        <ThreeColRow label="Residence — State (flag only)"
          position={res.positionState}
          candidate={res.candidateState}
          score={stateScore} />
        <ThreeColRow label="Residence — City (flag only)"
          position={res.positionCity}
          candidate={res.candidateCity}
          score={cityScore} />
      </ThreeColTable>
    );
  };

  /* ── Render Preferences ─────────────────────────────────── */
  const renderPreference = (dim, withData, prefTopW) => {
    let pos = dim.positionValue;
    let cand = dim.seekerValue;
    if (dim.name === 'workplaceLocation') {
      pos = fmtAddress(pos);
      cand = fmtLocations(cand);
    } else if (dim.name === 'salary') {
      pos = fmtSalaryRange(pos || {});
      cand = fmtSalaryRange(cand || {});
    } else {
      pos = Array.isArray(pos) ? fmtList(pos) : (pos == null ? '—' : String(pos));
      cand = Array.isArray(cand) ? fmtList(cand) : (cand == null ? '—' : String(cand));
    }
    const muted = dim.result === 'no_data';
    const contrib = dim.score != null && withData > 0
      ? (dim.score / withData) * prefTopW
      : null;
    return (
      <ThreeColRow key={dim.name}
        label={`${PREF_LABELS[dim.name] || dim.name} — ${dim.result}`}
        position={pos} candidate={cand}
        score={dim.score} muted={muted}
        contribution={contrib} />
    );
  };

  const renderPreferences = () => {
    const dims = Array.isArray(prefs.dimensions) ? prefs.dimensions : [];
    if (!dims.length) return <div style={{ fontSize: 12, color: '#999' }}>No preference data.</div>;
    const withData = prefs.dimensionsWithData || dims.filter(d => d.result !== 'no_data').length;
    const prefTopW = topW.preferences || 0;
    return <ThreeColTable>{dims.map(d => renderPreference(d, withData, prefTopW))}</ThreeColTable>;
  };

  /* ── Render Language sub-card ───────────────────────────── */
  const renderLanguage = () => {
    const matched = sub.language?.matched || [];
    const missing = sub.language?.missing || [];
    if (!matched.length && !missing.length) return <div style={{ fontSize: 12, color: '#999' }}>No language data.</div>;

    const langScale = (subW.language || 0) * (topW.skillsAbilities || 0);

    return (
      <ThreeColTable>
        {matched.map((l, i) => {
          const contribution = (l.score || 0) * langScale;
          return (
            <ThreeColRow key={`m-${i}`}
              label={`${l.name} (${l.requirement || 'unspecified'}) — matched`}
              position={`Required: ${l.requiredProficiency || '—'}`}
              candidate={`Candidate: ${l.candidateProficiency || '—'}`}
              score={l.score}
              contribution={Math.round(contribution * 10) / 10} />
          );
        })}
        {missing.map((l, i) => (
          <ThreeColRow key={`x-${i}`}
            label={`${l.name} (${l.requirement || 'unspecified'}) — missing`}
            position={l.name} candidate="Not on profile" score={0}
            contribution={0} />
        ))}
      </ThreeColTable>
    );
  };

  /* ── Render Attributes sub-card ─────────────────────────── */
  const renderAttributes = () => {
    const matched = sub.attributes?.matched || [];
    const missing = sub.attributes?.missing || [];
    if (!matched.length && !missing.length) return <div style={{ fontSize: 12, color: '#999' }}>No attribute data.</div>;

    const attrScale = (subW.attributes || 0) * (topW.skillsAbilities || 0);

    return (
      <ThreeColTable>
        {matched.map((a, i) => {
          const contribution = (a.score || 0) * attrScale;
          return (
            <ThreeColRow key={`m-${i}`}
              label={`${a.requirement || 'unspecified'} — matched`}
              position={a.name} candidate={a.name} score={a.score || 100}
              contribution={Math.round(contribution * 10) / 10} />
          );
        })}
        {missing.map((a, i) => (
          <ThreeColRow key={`x-${i}`}
            label={`${a.requirement || 'unspecified'} — missing`}
            position={a.name} candidate="Not on profile" score={0}
            contribution={0} />
        ))}
      </ThreeColTable>
    );
  };

  return (
    <div className="fade-in page-content-wide">
      <div className="page-header"><h1>Candidate Match Review</h1></div>

      <div style={{ paddingBottom: 6 }}>
        <button onClick={() => navigate('/employer/candidates/reviews')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, padding: '4px 0', fontFamily: 'inherit' }}>
          <ArrowLeft size={13} /> All Candidates
        </button>
      </div>

      {/* ── Header card ── */}
      <div className="section-box" style={{ marginBottom: 16 }}>
        <div className="section-box-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{seeker.firstName} {seeker.lastName}</span>
          <span style={{ padding: '3px 14px', borderRadius: 12, fontSize: 14, fontWeight: 700, color: '#fff', background: BAND_COLOURS[match.matchBand] || '#999' }}>
            {Math.round(match.totalScore)}% — {match.matchBand || 'N/A'}
          </span>
        </div>
        <div className="section-box-body" style={{ padding: '14px 18px' }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13, color: '#555' }}>
            <div><strong>Job Ad:</strong> {ad.title} {ad.adNumber != null && <span style={{ color: '#999' }}>#{String(ad.adNumber).padStart(4, '0')}</span>}</div>
            <div><strong>Position:</strong> {posName}</div>
            <div><strong>Workplace:</strong> {workplace.name || '—'}</div>
            <div><strong>Employer:</strong> {groupProf.profileName || '—'}</div>
            <div><strong>Model:</strong> {breakdown.modelName || '—'}</div>
            <div><strong>Status:</strong> <span className={`tag ${match.status === 'SHORTLISTED' ? 'tag-green' : match.status === 'REJECTED' ? 'tag-red' : 'tag-ghost'}`}>{match.status}</span></div>
          </div>
        </div>
      </div>

      {/* ── Score Overview ── */}
      <div className="section-box" style={{ marginBottom: 16 }}>
        <div className="section-box-header">Score Overview</div>
        <div className="section-box-body" style={{ padding: '18px 18px 6px' }}>
          {topScores.map(s => (
            <ScoreBar key={s.key} label={s.label} score={s.score || 0} weight={s.weight || 0} weightedScore={(s.score || 0) * (s.weight || 0)} />
          ))}
          <div style={{ borderTop: '2px solid #ddd', paddingTop: 10, marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>Total Weighted Score</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: BAND_COLOURS[match.matchBand] || '#333' }}>
              {Math.round(totalWeighted * 10) / 10}%
            </span>
          </div>
        </div>
      </div>

      {/* ── 1. Circumstances ── */}
      <Section title="Circumstances" score={circ.score} weightLabel={`weight ${((topW.circumstances || 0) * 100).toFixed(0)}%`}>
        {renderCircumstances()}
      </Section>

      {/* ── 2. Seeker Preferences ── */}
      <Section title="Seeker Preferences" score={prefs.score} weightLabel={`weight ${((topW.preferences || 0) * 100).toFixed(0)}%`}>
        {renderPreferences()}
      </Section>

      {/* ── 3. Skills & Abilities (composite) ── */}
      <Section title="Skills & Abilities" score={sa.score} weightLabel={`weight ${((topW.skillsAbilities || 0) * 100).toFixed(0)}%`}>
        <SubCard title={`Abilities · weight ${((subW.abilities || 0) * 100).toFixed(0)}%`} score={sub.abilities?.score} breakdown={sub.abilities}>
          <EmbeddingGradeRows gradeScores={sub.abilities?.gradeScores} scaleFactor={(subW.abilities || 0) * (topW.skillsAbilities || 0)} />
        </SubCard>
        <SubCard title={`Raw Skills · weight ${((subW.rawSkills || 0) * 100).toFixed(0)}%`} score={sub.rawSkills?.score} breakdown={sub.rawSkills}>
          <EmbeddingGradeRows gradeScores={sub.rawSkills?.gradeScores} scaleFactor={(subW.rawSkills || 0) * (topW.skillsAbilities || 0)} />
        </SubCard>
        <SubCard title={`Education · weight ${((subW.education || 0) * 100).toFixed(0)}%`} score={sub.education?.score} breakdown={sub.education}>
          <EmbeddingGradeRows gradeScores={sub.education?.gradeScores} scaleFactor={(subW.education || 0) * (topW.skillsAbilities || 0)} />
        </SubCard>
        <SubCard title={`Experience · weight ${((subW.experience || 0) * 100).toFixed(0)}%`} score={sub.experience?.score} breakdown={sub.experience}>
          <EmbeddingGradeRows gradeScores={sub.experience?.gradeScores} scaleFactor={(subW.experience || 0) * (topW.skillsAbilities || 0)} />
        </SubCard>
        <SubCard title={`Language · weight ${((subW.language || 0) * 100).toFixed(0)}%`} score={sub.language?.score} breakdown={sub.language}>
          {renderLanguage()}
        </SubCard>
        <SubCard title={`Attributes · weight ${((subW.attributes || 0) * 100).toFixed(0)}%`} score={sub.attributes?.score} breakdown={sub.attributes}>
          {renderAttributes()}
        </SubCard>
      </Section>

      {/* ── Flags ── */}
      {Array.isArray(match.flags) && match.flags.length > 0 && (
        <Section title="Flags" defaultOpen>
          {match.flags.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: f.type === 'RED' ? '#CC0000' : f.type === 'ORANGE' ? '#E6A817' : '#FFD700',
              }} />
              <span style={{ fontSize: 13, color: '#444' }}><strong>{f.field}:</strong> {f.reason}</span>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
};
